import { useEffect, useState } from "react";
import { CUSTOM_FIELD_RULE_PREFIX, CUSTOMER_STANDARD_FIELDS, CUSTOMER_STANDARD_FIELD_LABEL } from "@usales/contract/values";
import { fetchCustomerFieldSettings, type CustomerFieldDef, type CustomFieldValues, type StandardCustomerFieldDef } from "../api.ts";

/**
 * 企业自定义客户字段在界面上的公共件(2026-10-20 产品方案会 40:04):
 * 按定义的类型显示与编辑 —— 数字用数字框、日期用日期框、单选用下拉、文本用输入框。
 *
 * 值的归一化在后端(`coerceCustomValue`):这里只负责把人填的东西原样送过去,
 * 后端拒了就把原因显示出来。所以 number 框里输什么都先存成字符串,不在前端 `Number()` ——
 * 「5000万」这类带单位的要让后端报「不是数字」,静默转成 5000 正是后端刻意避免的。
 */
export const customFieldRef = (key: string): string => `${CUSTOM_FIELD_RULE_PREFIX}${key}`;
export const isCustomFieldRef = (name: string): boolean => name.startsWith(CUSTOM_FIELD_RULE_PREFIX);
export const customFieldKeyOf = (ref: string): string => ref.slice(CUSTOM_FIELD_RULE_PREFIX.length);

/** 把 `cf.<key>`(或裸 key)翻成企业起的名字;找不到定义时回原文,别让一个丢了定义的 key 把整行炸掉。 */
export const customFieldLabel = (defs: readonly CustomerFieldDef[], keyOrRef: string): string => {
  const key = isCustomFieldRef(keyOrRef) ? customFieldKeyOf(keyOrRef) : keyOrRef;
  return defs.find((d) => d.key === key)?.label ?? keyOrRef;
};

/** 读本租户的字段定义。默认连归档的一起给(客户上可能还有它的值要翻名字);编辑、筛选只用未归档的。 */
export function useCustomerFields(includeArchived = true): { fields: CustomerFieldDef[]; standardFields: StandardCustomerFieldDef[]; loaded: boolean; error: string; reload: () => void } {
  const [fields, setFields] = useState<CustomerFieldDef[]>([]);
  const [standardFields, setStandardFields] = useState<StandardCustomerFieldDef[]>(()=>CUSTOMER_STANDARD_FIELDS.map(key=>({key,label:CUSTOMER_STANDARD_FIELD_LABEL[key],type:"text",required:key==="name",protected:key==="name",archivedAt:null})));
  // 「还没加载」和「加载完是空的」要分开:企业一个自定义字段都没有时,列表为空是正常结果,不是还在转圈
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(()=>{const changed=()=>setVersion(v=>v+1);window.addEventListener("customer-fields-changed",changed);window.addEventListener("focus",changed);return()=>{window.removeEventListener("customer-fields-changed",changed);window.removeEventListener("focus",changed);};},[]);
  useEffect(() => {
    let active = true;
    void fetchCustomerFieldSettings(includeArchived).then((data) => { if (active) { setFields(data.fields); if(data.standardFields)setStandardFields(data.standardFields);setError(""); setLoaded(true); } }).catch((e) => { if (active) setError((e as Error).message); });
    return () => { active = false; };
  }, [includeArchived, version]);
  return { fields, standardFields, loaded, error, reload: () => window.dispatchEvent(new Event("customer-fields-changed")) };
}

export const activeFields = (defs: readonly CustomerFieldDef[]): CustomerFieldDef[] => defs.filter((d) => !d.archivedAt);

/** 显示用:number 按本地千分位,其余原样;空值统一「—」。 */
export const formatCustomValue = (def: Pick<CustomerFieldDef, "type">, value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (def.type === "number") { const n = Number(value); return Number.isFinite(n) ? n.toLocaleString("zh-CN", { maximumFractionDigits: 6 }) : String(value); }
  return String(value);
};

/**
 * 一个字段的编辑框。`value` 是草稿里的字符串(空串 = 清空),`onChange` 回字符串。
 * 单选里已存的值不在选项中时(主管后来删了那个选项)仍然列出来并标「不再是选项」——
 * 不然下拉会默认跳到第一个选项,人一保存就把客户的旧值悄悄改掉了。
 */
export function CustomFieldInput(props: { def: CustomerFieldDef; value: string; onChange: (value: string) => void; ariaLabel: string; disabled?: boolean }): React.JSX.Element {
  const { def, value, onChange, ariaLabel } = props;
  const disabled = props.disabled ?? false;
  if (def.type === "select") {
    const stale = value !== "" && !def.options.includes(value);
    return <select className="in" aria-label={ariaLabel} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">未填写</option>
      {stale && <option value={value}>{value}(不再是选项)</option>}
      {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>;
  }
  if (def.type === "date") return <input className="in" type="date" aria-label={ariaLabel} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  if (def.type === "number") return <input className="in" type="text" inputMode="decimal" aria-label={ariaLabel} placeholder="只填数字,不带单位" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  return <input className="in" type="text" aria-label={ariaLabel} maxLength={500} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
}

/** 把客户上的值变成编辑草稿:每个未归档字段一个字符串。 */
export const customDraftOf = (defs: readonly CustomerFieldDef[], values: CustomFieldValues | null | undefined): Record<string, string> =>
  Object.fromEntries(activeFields(defs).map((d) => [d.key, values?.[d.key] === undefined ? "" : String(values[d.key])]));

/**
 * 草稿 → 提交体:只送改过的字段,空串送 `null`(清空)。什么都没改就回 undefined,
 * 调用方据此不带 `customFields`,免得后端为了一个没变的对象多写一条变更记录。
 */
export function customPatchOf(defs: readonly CustomerFieldDef[], draft: Record<string, string>, original: CustomFieldValues | null | undefined): Record<string, string | null> | undefined {
  const out: Record<string, string | null> = {};
  for (const d of activeFields(defs)) {
    const next = (draft[d.key] ?? "").trim(), before = original?.[d.key];
    if ((before === undefined ? "" : String(before)) === next) continue;
    out[d.key] = next === "" ? null : next;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * 详情页的只读列表:按定义顺序列全部未归档字段(没值的显示「—」,这样人知道有这个字段可填),
 * 归档字段只在客户上还有值时才出现并标灰 —— 它们不再可编辑,但历史值不该凭空消失。
 */
export function CustomFieldList(props: { defs: readonly CustomerFieldDef[]; values: CustomFieldValues | null | undefined }): React.JSX.Element | null {
  const rows = props.defs.filter((d) => !d.archivedAt || props.values?.[d.key] !== undefined);
  if (!rows.length) return null;
  return <dl className="custom-field-list" aria-label="企业自定义字段">{rows.map((d) => <div key={d.key} className={d.archivedAt ? "archived" : ""}><dt>{d.label}{d.archivedAt && <small>已归档</small>}</dt><dd>{formatCustomValue(d, props.values?.[d.key])}</dd></div>)}</dl>;
}
